import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, ShoppingCartIcon, TrashIcon, CheckIcon, ListIcon, FolderIcon, FolderPlusIcon, LayoutGridIcon, BookIcon, BookOpenIcon, HeadphonesIcon, FlameIcon, MoonIcon, CalendarIcon, ClockIcon, BellIcon, TabletIcon, MenuIcon, SearchIcon, XIcon } from 'lucide-react';
import SaveToTracker from '../components/UI/SaveToTracker';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
type ItemType = 'Book' | 'Audiobook' | 'Service' | 'Merch';
type Format = 'Paperback' | 'Hardcover' | 'eBook' | 'Audiobook';
type SortOption = 'recent' | 'title' | 'author' | 'price-asc' | 'price-desc';
type ViewMode = 'grid' | 'list';
interface WishlistItem {
  id: number;
  title: string;
  subtitle?: string;
  author: string;
  coverImage: string;
  price: number;
  format: Format;
  type: ItemType;
  status: 'Available' | 'Preorder' | 'Not Yet Released';
  genres: string[];
  dateAdded: string;
  description: string;
  folderId?: string;
}
interface Folder {
  id: string;
  name: string;
  itemCount: number;
}
const mockWishlistItems: WishlistItem[] = [{
  id: 1,
  title: 'The Silent Echo',
  subtitle: 'A Mystery Thriller',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 14.99,
  format: 'Paperback',
  type: 'Book',
  status: 'Available',
  genres: ['Mystery', 'Thriller'],
  dateAdded: '2024-01-15',
  description: 'A gripping mystery that will keep you on the edge of your seat until the very last page.'
}, {
  id: 2,
  title: 'Beyond the Veil',
  subtitle: 'Book 1 of the Mystic Series',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 9.99,
  format: 'eBook',
  type: 'Book',
  status: 'Preorder',
  genres: ['Fantasy', 'Young Adult'],
  dateAdded: '2024-01-16',
  description: 'Dive into a world of magic and mystery in this enchanting new fantasy series.'
}];
const initialFolders: Folder[] = [{
  id: 'all',
  name: 'All Items',
  itemCount: mockWishlistItems.length
}, {
  id: 'to-read',
  name: 'To Read',
  itemCount: 0
}, {
  id: 'favorites',
  name: 'Favorites',
  itemCount: 0
}];
const Wishlist = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [formatFilter, setFormatFilter] = useState<Format | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentFolder, setCurrentFolder] = useState('all');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [folders, setFolders] = useState([{
    id: 'all',
    name: 'All Items',
    itemCount: 15
  }, {
    id: 'favorites',
    name: 'Favorites',
    itemCount: 5
  }, {
    id: 'to-read',
    name: 'To Read',
    itemCount: 8
  }, {
    id: 'romance',
    name: 'Romance',
    itemCount: 4
  }, {
    id: 'fantasy',
    name: 'Fantasy',
    itemCount: 6
  }, {
    id: 'mystery',
    name: 'Mystery',
    itemCount: 3
  }]);
  const [items, setItems] = useState([{
    id: 1,
    title: 'The Silent Echo',
    author: 'Elena Richards',
    coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    price: 24.99,
    format: 'Paperback',
    status: 'Available',
    folder: 'all',
    description: 'A gripping new thriller from bestselling author Elena Richards that will keep you on the edge of your seat.',
    genres: ['Mystery', 'Thriller'],
    releaseDate: '2023-10-01',
    rating: 4.8,
    reviews: 45,
    heatLevel: 3,
    series: {
      name: 'Echo Series',
      position: 1
    }
  }, {
    id: 2,
    title: 'Beyond the Veil',
    author: 'Marcus Chen',
    coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    price: 19.99,
    format: 'eBook',
    status: 'Available',
    folder: 'all',
    description: 'Discover a world of magic and romance in this enchanting fantasy novel by Marcus Chen.',
    genres: ['Fantasy', 'Romance'],
    releaseDate: '2023-09-28',
    rating: 4.6,
    reviews: 32,
    heatLevel: 4
  }, {
    id: 3,
    title: 'Whispers in the Dark',
    author: 'Sarah Johnson',
    coverImage: 'https://images.unsplash.com/photo-1621351183012-e2f9972dd9bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    price: 22.99,
    format: 'Hardcover',
    status: 'Available',
    folder: 'favorites',
    description: 'A chilling tale of supernatural horror that will haunt your dreams long after you finish reading.',
    genres: ['Horror', 'Supernatural'],
    releaseDate: '2023-10-15',
    rating: 4.9,
    reviews: 28,
    heatLevel: 4
  }, {
    id: 4,
    title: 'The Lost City',
    author: 'David Williams',
    coverImage: 'https://images.unsplash.com/photo-1531928351158-2f736078e0a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    price: 21.99,
    format: 'Audiobook',
    status: 'Pre-order',
    folder: 'to-read',
    description: 'An epic adventure through uncharted territories and ancient civilizations that will transport you to another world.',
    genres: ['Adventure', 'Historical Fiction'],
    releaseDate: '2023-11-20',
    rating: 0,
    reviews: 0,
    heatLevel: 2
  }, {
    id: 5,
    title: 'Starfall',
    author: 'Amara Knight',
    coverImage: 'https://images.unsplash.com/photo-1518744386442-2d48ac55263f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    price: 18.99,
    format: 'eBook',
    status: 'Available',
    folder: 'romance',
    description: 'A steamy sci-fi romance that explores love across the stars and the boundaries of different worlds.',
    genres: ['Sci-Fi', 'Romance'],
    releaseDate: '2023-09-25',
    rating: 4.7,
    reviews: 39,
    heatLevel: 5
  }, {
    id: 6,
    title: 'The Hidden Truth',
    author: 'Thomas Lee',
    coverImage: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    price: 16.99,
    format: 'Paperback',
    status: 'Not Yet Released',
    folder: 'mystery',
    description: 'A mind-bending mystery that will keep you guessing until the very last page.',
    genres: ['Mystery', 'Crime'],
    releaseDate: '2023-12-05',
    rating: 0,
    reviews: 0,
    heatLevel: 2
  }]);
  const filteredItems = items.filter(item => {
    // First apply folder, format and status filters
    if (currentFolder !== 'all' && item.folder !== currentFolder && item.folder !== 'all') return false;
    if (formatFilter !== 'all' && item.format !== formatFilter) return false;
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    // Then apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      return item.title.toLowerCase().includes(query) || item.author.toLowerCase().includes(query) || item.genres.some(genre => genre.toLowerCase().includes(query)) || item.description.toLowerCase().includes(query);
    }
    return true;
  });
  const toggleItemSelection = (id: number) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };
  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    const newFolder = {
      id: newFolderName.toLowerCase().replace(/\s+/g, '-'),
      name: newFolderName,
      itemCount: 0
    };
    setFolders([...folders, newFolder]);
    setNewFolderName('');
    setShowNewFolderDialog(false);
  };
  const updateFolderCounts = () => {
    const counts = {};
    items.forEach(item => {
      if (!counts[item.folder]) counts[item.folder] = 0;
      counts[item.folder]++;
    });
    const allCount = items.length;
    setFolders(folders.map(folder => ({
      ...folder,
      itemCount: folder.id === 'all' ? allCount : counts[folder.id] || 0
    })));
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Available':
        return 'bg-green-600 text-white';
      case 'Pre-order':
        return 'bg-yellow-600 text-white';
      case 'Not Yet Released':
        return 'bg-indigo-600 text-white';
      default:
        return 'bg-gray-600 text-white';
    }
  };
  useEffect(() => {
    updateFolderCounts();
  }, []);
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [currentFolder, formatFilter, statusFilter]);
  return <div className={`min-h-screen py-6 sm:py-8 ${dk.page}`} data-id="element-22142" data-mp="8kbkznkcse6d">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-22143" data-mp="4h5j0s49x95p">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4" data-id="element-22144" data-mp="4gm1vdmg6osq">
          <div data-id="element-22145" data-mp="3vpt4d82ikwd9">
            <h1 className={`text-xl sm:text-2xl font-bold ${dk.textHeading}`} data-id="element-22146" data-mp="3vq2ls79htv7c">
              Your Wishlist
            </h1>
            <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-22147" data-mp="39mgajlbzpzu">
              {currentFolder === 'all' ? `All saved items (${filteredItems.length})` : `${folders.find(f => f.id === currentFolder)?.name} (${filteredItems.length})`}
            </p>
          </div>
          {/* Search Bar */}
          <div className="order-3 sm:order-2 w-full sm:w-auto" data-id="element-22148" data-mp="3lfr2apezvipw">
            <div className="relative" data-id="element-22149" data-mp="3gfuvyjitrf76">
              <input type="text" placeholder="Search by title, author, genre..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.input}`} data-id="element-22150" data-mp="3ggwgrfpn7g23" />
              <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-22151" data-mp="15micr9x88hbv" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${dk.textMuted} hover:${dk.textSecondary}`} data-id="element-22152" data-mp="1i6fkyb07e15p">
                  <XIcon className="h-4 w-4" data-id="element-22153" data-mp="188re4mpbp376" />
                </button>}
            </div>
          </div>
          <div className="order-2 sm:order-3 flex gap-3 flex-wrap" data-id="element-22154" data-mp="1vyc800n0lkv8">
            <button onClick={() => setIsEditing(!isEditing)} aria-pressed={isEditing} aria-label="Toggle edit mode" className={`inline-flex items-center justify-center gap-2 px-4 py-2 border shadow-sm text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-150 ${isEditing ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700' : `${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.surfaceMuted}`}`} data-id="element-22155" data-mp="20vltj57a9cds">
              {isEditing ? 'Cancel Edit' : 'Edit Items'}
            </button>
            <button onClick={() => setShowNewFolderDialog(true)} aria-label="Create new folder" className={`inline-flex items-center justify-center gap-2 px-4 py-2 border shadow-sm text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-150 ${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.surfaceMuted}`} data-id="element-22156" data-mp="2ki6ylapvrp1u">
              <FolderPlusIcon className="h-5 w-5 text-indigo-500" data-id="element-22157" data-mp="21t0mw7ni7xpj" />
              New Folder
            </button>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle sidebar" className={`inline-flex items-center justify-center gap-2 px-4 py-2 border shadow-sm text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-150 md:hidden ${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.surfaceMuted}`} data-id="element-22158" data-mp="1wu5se8g6ckpl">
              <MenuIcon className="h-5 w-5 text-indigo-500" data-id="element-22159" data-mp="1hrxncstfpvla" />
              Folders
            </button>
          </div>
        </header>

        <div className="flex flex-col md:flex-row gap-6" data-id="element-22160" data-mp="1rok6nd644el7">
          <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`} data-id="element-22161" data-mp="1rlxzvfr1q341">
            <div className={`rounded-lg shadow-sm border overflow-hidden ${dk.card}`} data-id="element-22162" data-mp="yz3iwstftlvb">
              <div className={`p-4 border-b ${dk.border}`} data-id="element-22163" data-mp="13xwpe1ab4clz">
                <h2 className={`font-semibold ${dk.textHeading}`} data-id="element-22164" data-mp="13wli3rhnkmq4">Folders</h2>
              </div>
              <nav className="p-2" data-id="element-22165" data-mp="18r967p8qvf46">
                <ul className="space-y-1" data-id="element-22166" data-mp="baakmppcf1xj">
                  {folders.map(folder => <li key={folder.id} data-id="element-22167" data-mp="g8lqkpciosah">
                      <button onClick={() => {
                    setCurrentFolder(folder.id);
                    setSidebarOpen(false);
                  }} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${currentFolder === folder.id ? dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-800' : `${dk.textSecondary} ${dk.dropdownItemHover}`}`} data-id="element-22168" data-mp="g7ap4w6jnwmj">
                        <FolderIcon className={`h-5 w-5 ${currentFolder === folder.id ? 'text-indigo-600 dark:text-indigo-400' : dk.textMuted}`} data-id="element-22169" data-mp="30mpoh24bj0h4" />
                        <span className="flex-1 truncate" data-id="element-22170" data-mp="3alx8271nbrco">{folder.name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${currentFolder === folder.id ? dk.isDark ? 'bg-indigo-900/50 text-indigo-200' : 'bg-indigo-200 text-indigo-800' : dk.badgeNeutral}`} data-id="element-22171" data-mp="35ox1zw4sqolk">
                          {folder.itemCount}
                        </span>
                      </button>
                    </li>)}
                </ul>
              </nav>
            </div>
          </div>

          <div className="flex-1" data-id="element-22172" data-mp="3d63dyk9tlrvv">
            <section aria-label="Filter and view wishlist items" className={`mb-6 p-4 rounded-lg shadow-sm border ${dk.card}`} data-id="element-22173" data-mp="3d6v0z6pcqhaj">
              <div className="flex flex-wrap gap-4 items-center justify-between" data-id="element-22174" data-mp="345f29w7wg3m5">
                <div className="flex flex-wrap gap-4 items-center" data-id="element-22175" data-mp="395t8d8pxx3hj">
                  <label htmlFor="formatFilter" className="sr-only" data-id="element-22176" data-mp="2u8ab1hz75hyh">
                    Filter by format
                  </label>
                  <select id="formatFilter" value={formatFilter} onChange={e => setFormatFilter(e.target.value as Format | 'all')} className={`rounded-lg border py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-22177" data-mp="2z6k85y4hesmq">
                    <option value="all" data-id="element-22178" data-mp="qy5k7uu2awsg">All Formats</option>
                    <option value="Paperback" data-id="element-22179" data-mp="m041jb0jszhi">Paperback</option>
                    <option value="Hardcover" data-id="element-22180" data-mp="m2qb1g701p2d">Hardcover</option>
                    <option value="eBook" data-id="element-22181" data-mp="i0dftlpvp9ju">eBook</option>
                    <option value="Audiobook" data-id="element-22182" data-mp="1epb8mye2ixed">Audiobook</option>
                  </select>
                  <label htmlFor="statusFilter" className="sr-only" data-id="element-22183" data-mp="1er5pa0ib43cn">
                    Filter by availability
                  </label>
                  <select id="statusFilter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`rounded-lg border py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-22184" data-mp="14yq6x233lcr8">
                    <option value="all" data-id="element-22185" data-mp="3bbu1kcwhvs2r">All Availability</option>
                    <option value="Available" data-id="element-22186" data-mp="3bdexa5ujph27">Available Now</option>
                    <option value="Pre-order" data-id="element-22187" data-mp="3ga5dct6cocnm">Pre-order</option>
                    <option value="Not Yet Released" data-id="element-22188" data-mp="3kcigd9tppwgp">Coming Soon</option>
                  </select>
                </div>

                <div className={`flex border rounded-lg overflow-hidden ${dk.border}`} data-id="element-22189" data-mp="3pc4tlj9jjcpi">
                  <button onClick={() => setViewMode('list')} className={`p-2 ${viewMode === 'list' ? dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-600' : `${dk.surface} ${dk.textSecondary}`}`} data-id="element-22190" data-mp="3u9n1r7fqhnwg">
                    <ListIcon className="h-5 w-5" data-id="element-22191" data-mp="bwyren02r07g" />
                  </button>
                  <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-100 text-indigo-600' : `${dk.surface} ${dk.textSecondary}`}`} data-id="element-22192" data-mp="gw1yeczm7gi2">
                    <LayoutGridIcon className="h-5 w-5" data-id="element-22193" data-mp="3ny0w8t6u5md9" />
                  </button>
                </div>
              </div>
            </section>

            {/* Items List */}
            {viewMode === 'grid' ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-22194" data-mp="1qapkfuecb2g">
                {filteredItems.map(item => <div key={item.id} className="relative" data-id="element-22195" data-mp="3a7famwpzyybo">
                    <UnifiedBookCard book={{
                ...item,
                id: String(item.id),
                coverImage: item.coverImage,
                rating: item.rating,
                genres: item.genres,
                spiceLevel: item.heatLevel,
                darknessLevel: 0,
                seriesName: item.series?.name,
                seriesNumber: item.series?.position,
                totalSeriesBooks: 0
              }} data-id="element-22196" data-mp="357sx0m69bg2c" />
                    {isEditing && <div className="absolute top-2 right-2 z-20" data-id="element-22197" data-mp="2h4wa20r066fv">
                        <button onClick={e => {
                  e.preventDefault();
                  toggleItemSelection(item.id);
                }} className={`p-1.5 rounded-full ${selectedItems.includes(item.id) ? 'bg-indigo-600 text-white' : 'bg-white text-gray-400 border border-gray-300'}`} data-id="element-22198" data-mp="22f6x789jsdzr">
                          <CheckIcon className="h-4 w-4" data-id="element-22199" data-mp="2l5y29xmi5i8c" />
                        </button>
                      </div>}
                  </div>)}
              </div> : <div className="space-y-4" data-id="element-22200" data-mp="19p80qrt0xel2">
                {filteredItems.map(item => <div key={item.id} className={`flex gap-4 p-4 rounded-lg shadow-sm border ${dk.card}`} data-id="element-22201" data-mp="1epmacea9wwl7">
                    <img src={item.coverImage} alt={item.title} className="w-20 h-32 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-22202" data-mp="jjqrbhu5up9l" />
                    <div className="flex-1 min-w-0 py-1" data-id="element-22203" data-mp="aij4px10bcvc">
                      <div className="flex justify-between items-start" data-id="element-22204" data-mp="al5d1yvq1wem">
                        <div data-id="element-22205" data-mp="3rxzfmdpngtvy">
                          <h3 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-22206" data-mp="3rxplqf1hi2kz">
                            {item.title}
                          </h3>
                          <p className={`text-sm ${dk.textSecondary}`} data-id="element-22207" data-mp="3izvw1my8676s">
                            {item.author}
                          </p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-22208" data-mp="3904y1bagha1y">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusColor(item.status)}`} data-id="element-22209" data-mp="3930vkyie5wpv">
                            {item.status}
                          </span>
                          {isEditing && <button onClick={() => toggleItemSelection(item.id)} className={`p-1.5 rounded-full ${selectedItems.includes(item.id) ? 'bg-indigo-600 text-white' : 'bg-white text-gray-400 border border-gray-300'}`} data-id="element-22210" data-mp="2pgzeorg1f4oa">
                              <CheckIcon className="h-4 w-4" data-id="element-22211" data-mp="38dqqygx5wjiu" />
                            </button>}
                        </div>
                      </div>
                      <p className={`text-sm mt-2 line-clamp-2 ${dk.textMuted}`} data-id="element-22212" data-mp="3n4pzxt1wta2y">
                        {item.description}
                      </p>
                      <div className="mt-auto pt-4 flex items-center justify-between" data-id="element-22213" data-mp="3w18l2j3wftoz">
                        <div className="flex items-center gap-4 text-sm" data-id="element-22214" data-mp="3s3kozcqgzuvi">
                          <span className={`font-bold ${dk.textHeading}`} data-id="element-22215" data-mp="30v1jearq7rw">
                            ${item.price}
                          </span>
                          <span className={dk.textMuted} data-id="element-22216" data-mp="cxq1m1mowg8a">{item.format}</span>
                        </div>
                        <div className="flex gap-2" data-id="element-22217" data-mp="ho6dhhav3tsj">
                          <button className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ${dk.textSecondary}`} data-id="element-22218" data-mp="hpzkwytaui1l">
                            <TrashIcon className="h-4 w-4" data-id="element-22219" data-mp="qntg26fbv5n6" />
                          </button>
                          <button className={`p-2 rounded-full hover:bg-indigo-50 dark:hover:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400`} data-id="element-22220" data-mp="vp92o2x5lk7o">
                            <ShoppingCartIcon className="h-4 w-4" data-id="element-22221" data-mp="2xgvvma0lvp6s" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div>}

            {filteredItems.length === 0 && <div className="text-center py-12" data-id="element-22222" data-mp="1es16sro11n0r">
                <p className={dk.textMuted} data-id="element-22223" data-mp="1aqzekusj2b8a">
                  No items found matching your criteria.
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default Wishlist;